import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Params, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@components/layout/page-header';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useThemeContext } from '@utils/hooks/use-theme-context';

import { useGetModels } from '@utils/api/models/use-get-models';
import { PATHS } from '../../router/paths';
import { formatDate } from '@utils/date';
import {
  SubOrganization,
  useGetCustomersSubOrganizationsBo
} from '@utils/api/back-office/customer-bo/use-get-reseller-suborganizations';

import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo';

type Row = {
  id: number | string;
  name: string;
  email: string;
  registration_date: string;
  is_active: boolean;
  is_deleted: boolean;
  country: string | null;
  parent_reseller: string;
};

export default function ResellerBoSubOrganizations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const { data: supportedCountries } = useGetModels();

  const { customerId } = useParams<Params>();
  const id = Number(customerId) || 0;

  const { data: subOrganizations, isLoading } =
    useGetCustomersSubOrganizationsBo(id);
  const { data: parentCustomer } = useGetCustomerById(id);
  const parentResellerName = parentCustomer?.result?.name ?? '—';

  const openSubOrg = (row: Row) => {
    if (row?.id) navigate(PATHS(String(row.id)).CUSTOMER_CONTENT);
  };

  const rows = useMemo<Row[]>(() => {
    if (!subOrganizations?.result || !Array.isArray(subOrganizations.result))
      return [];

    return subOrganizations.result.map((customer: SubOrganization) => {
      const countryKey = customer.country?.toLowerCase();
      const countryData = supportedCountries?.result.find(
        c => c.key === countryKey
      );
      const fullCountryName = countryData ? countryData.name : customer.country;

      return {
        id: customer.id,
        name: customer.name,
        email: customer.email?.toLowerCase?.() ?? customer.email,
        registration_date: formatDate(customer.registration_date || ''),
        is_active: customer.is_active,
        is_deleted: customer.is_deleted,
        country: fullCountryName ?? null,
        parent_reseller: parentResellerName
      };
    });
  }, [subOrganizations, supportedCountries, parentResellerName]);

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 110,
        minWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        cursor: 'pointer',
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('name'),
        field: 'name',
        minWidth: 240,
        enableSorting: false,
        cursor: 'pointer',
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        header: t('email'),
        field: 'email',
        minWidth: 240,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('registered'),
        field: 'registration_date',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('status'),
        field: 'is_active',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('active'), value: 'true' },
          { label: t('inactive'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const isActive = !!getValue();
          return (
            <StatusBadge
              status={isActive ? 'active' : 'inactive'}
              label={isActive ? t('active') : t('inactive')}
            />
          );
        }
      },
      {
        header: t('is-deleted'),
        field: 'is_deleted',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const isDeleted = !!getValue();
          if (!isDeleted) {
            return (
              <span
                className="inline-flex items-center rounded-full border border-hairline bg-app-soft px-2.5 py-0.5 text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('no')}
              </span>
            );
          }
          return <StatusBadge status="deleted" label={t('yes')} />;
        }
      },
      {
        header: t('country'),
        field: 'country',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('parent-reseller'),
        field: 'parent_reseller',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t, getColour]
  );

  return (
    <div className="bg-app flex-1">
      <PageHeader
        title={t('sub-organizations')}
        caption={t(
          'a-list-of-all-the-reseller-sub-organization-including-their-details'
        )}
        contentClassName="px-2"
      />

      <div>
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection={false}
          enablePagination
          enableSorting
          enableFiltering
          totalItems={subOrganizations?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="69vh"
          dense
          grid={false}
          striped
          loading={isLoading}
          onRowClick={openSubOrg}
          getRowId={row => String(row.id)}
        />
      </div>
    </div>
  );
}
